import isString from 'lodash/isString';
import { getJsonField } from '@/utilities/object';
import { makeGqlSuspenseQuery } from '@/utilities/gqlQuery';
import { type ResultOf, gql } from '@/utilities/gql';
import type { PrimaryCountryISO } from '@/constants/countries';
import { useLocation } from '@/hooks/useLocation';
import { convertExchange } from '../utils';
import { toCurrencyTruncated } from '@/utilities/formatting';

const QUERY = gql(
  `
	query CompanyStock($symbol: String!) {
		Company(symbol: $symbol) {
			# General
			id
			dateGenerated
			listingCurrencyISO
      isPrimaryListing # For server data
      exchangeCountryISO
			data {
				info
        listings # For server data
			}
			info {
				hasLogo
				primaryIndustryName
        ceoName
			}
      # Links
			canonicalURL
      nameSlug
      primaryIndustry {
        id
        slug
      }

			# Analysis (details)
			analysisValue {
				lastSharePrice
        marketCap
			}
			analysisHealth {
				debtToEquityRatio
			}
			analysisPast {
				earningsPerShare
				grossProfitMargin
				netIncomeMargin
				lastEarningsUpdate
			}
      analysisFuture {
        revenueGrowthAnnual
      #   nextEarningsRelease # Why is this unavailable on analysisPast?
      }
      analysisManagement {
        totalEmployees
      }
		}
	}
`
);

export type CommunityStock = ResultOf<typeof QUERY>;

export const getSymbol = (exchange: string, ticker: string) =>
  [convertExchange(exchange), ticker.toUpperCase()].join(':');

export const useCompanyStockCreator = makeGqlSuspenseQuery(QUERY, {
  tags: ['company-stock'],
});

export const useCompanyStock = () => {
  const location = useLocation();

  const { data, status } = useCompanyStockCreator(
    { symbol: getSymbol(location.payload.exchange, location.payload.ticker) },
    { staleTime: 1000 * 60 * 60 * 24 }
  );

  const { Company: company } = data || {};

  // Assertions for `company.data.info` as the types aren't supplied by the API
  // TODO: Zodify this
  const assertedInfoFields = {
    name: getJsonField<string>(company?.data.info, 'name', isString),
    shortName: getJsonField<string>(company?.data.info, 'short_name', isString),
    description: getJsonField<string>(
      company?.data.info,
      'description',
      isString
    ),
    yearFunded: getJsonField<string>(
      company?.data.info,
      'year_founded',
      Number.isInteger
    ),
    exchangeSymbol: getJsonField<string>(
      company?.data.info,
      'exchange_symbol',
      isString
    ),
    tickerSymbol: getJsonField<string>(
      company?.data.info,
      'ticker_symbol',
      isString
    ),
    uniqueSymbol: getJsonField<string>(
      company?.data.info,
      'unique_symbol',
      isString
    ),
    url: getJsonField<string>(company?.data.info, 'url', isString),
    countryISO: getJsonField<PrimaryCountryISO>(
      company?.data.info,
      'country_iso',
      isString
    ),
  };

  return {
    status,

    ...assertedInfoFields,
    exchangeCountryISO: company?.exchangeCountryISO,
    companyId: company?.id,
    nameSlug: company?.nameSlug,
    canonicalUrl: company?.canonicalURL,
    dateGenerated: company?.dateGenerated,
    hasLogo: company?.info?.hasLogo ?? false,
    marketCap: company?.analysisValue?.marketCap
      ? toCurrencyTruncated(
          company?.analysisValue?.marketCap,
          1,
          company?.listingCurrencyISO
        )
      : 'n/a',
    rawMarketCap: company?.analysisValue?.marketCap,

    primaryIndustry: {
      id: company?.primaryIndustry?.id,
      slug: company?.primaryIndustry?.slug,
      name: company?.info?.primaryIndustryName,
    },
    listingCurrencyISO: company?.listingCurrencyISO as PrimaryCountryISO | null,
    totalEmployees: company?.analysisManagement?.totalEmployees,
    ceoName: company?.info?.ceoName,
    details: {
      // Value
      lastSharePrice: company?.analysisValue?.lastSharePrice,
      // Health
      debtToEquityRatio: company?.analysisHealth?.debtToEquityRatio,
      // Past
      earningsPerShare: company?.analysisPast?.earningsPerShare,
      grossProfitMargin: company?.analysisPast?.grossProfitMargin,
      netIncomeMargin: company?.analysisPast?.netIncomeMargin,
      lastEarningsUpdate: company?.analysisPast?.lastEarningsUpdate,
      // Future
      revenueGrowthAnnual: company?.analysisFuture?.revenueGrowthAnnual,
    },
  };
};
